import React, {
  memo, useState, useEffect,
  useCallback, useRef,
} from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { QuestionnaireRepository } from '../../api/repositories/Questionnaire';
import { UI_CONFIG, VIEW_MODE } from '../../constants/Constants';
import ErrorComponent from '../Common/Error';
import ReviewRespondentForm from '../ReviewRespondentForm';
import ReviewRespondentFormReadonly from '../ReviewRespondentFormReadonly';
import RespondentForm from '../RespondentForm';
import RespondentFormReadonly from '../RespondentFormReadonly';
import { SuccessModal } from '../Common';
import { ROUTE_NAME } from '../../constants/UserConstants';
import { getQueryParams } from '../../utils/HelperFunctions';
import FormView from '../FormView';
import { userConstants } from '../../locale/userTexts';

const QuestionnaireForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [formloader, setFormLoading] = useState(false);
  const [questionnaire, setQuestionnaire] = useState({
    title: '',
    descriptions: '',
  });
  const [questions, setAllQuestions] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [responseQuestionMap, setResponseQuestionMap] = useState({});
  const [respondentResponses, setRespondentResponses] = useState(null);
  const [searchParams, setSearchParams] = useState({});
  const [error, setError] = useState('');
  const [showModal, setModalState] = useState(false);
  const params = useParams();
  const history = useHistory();
  const { search } = useLocation();
  const targetRef = useRef();
  const [dimension, setDimension] = useState({ width: 0, height: 0 });
  const [lang, setLang] = useState('en');

  const onresize = useCallback(() => {
    // note i need to pass the event as an argument to the function
    const { body } = document;
    const html = document.documentElement;
    const width = html.offsetWidth;
    const height = Math.max(body.scrollHeight, body.offsetHeight,
      html.clientHeight, html.scrollHeight, html.offsetHeight);
    const htmlframeElement = window.parent;
    setDimension({ height, width });
    htmlframeElement.postMessage({
      type: 'RESIZE',
      data: { width, height },
    }, '*');
  }, [setDimension]);

  const resizeTimer = useCallback(
    () => setTimeout(() => {
      const { body } = document;
      const html = document.documentElement;
      const height = Math.max(body.scrollHeight, body.offsetHeight,
        html.clientHeight, html.scrollHeight, html.offsetHeight);
      if (dimension.height !== height) {
        onresize();
      }
    }, 0.5),
    [dimension.height, onresize],
  );

  useEffect(() => {
    const id = resizeTimer();
    return () => clearInterval(id);
  });

  useEffect(() => {
    window.addEventListener('resize', onresize, false);
    return () => {
      window.removeEventListener('resize', onresize, false);
    };
  }, [onresize]);

  // Per-order sessionStorage keys — each order gets its own key so going back and
  // re-answering order=1 overwrites only that order's entry without touching order=2.
  // eslint-disable-next-line arrow-body-style
  const getOrderStorageKey = (questionnaireId, respondentId, orderNum) => `qa_${questionnaireId}_${respondentId}_o${orderNum}`;

  const clearAllOrderKeys = (questionnaireId, respondentId) => {
    const prefix = `qa_${questionnaireId}_${respondentId}_o`;
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(prefix)) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  };

  // V2 questionnaires always have exactly 2 questions (order=1 and order=2)
  const V2_TOTAL_QUESTIONS = 2;

  const setQuestionnaireData = async (id, respondentId, view, responseId, order, isReviewing) => {
    try {
      setLoading(true);
      // Landing on order 1 normally means a fresh attempt, so the per-order cache is dropped.
      // WO-11861 added Back navigation, so the user can now arrive at order 1 mid-attempt —
      // clearing then would throw away the answers they already gave for later orders.
      if (order && Number(order) === 1 && !isReviewing) {
        clearAllOrderKeys(id, respondentId);
      }
      const questionnaireResp = await QuestionnaireRepository.getQuestionnaireById(id);
      if (questionnaireResp && questionnaireResp.questionnaire) {
        setQuestionnaire({ ...questionnaireResp.questionnaire });
        setLang(questionnaireResp.questionnaire.languageCode);
      }
      // Tracks the current order's question IDs so we can identify previous-order responses below
      const currentOrderQIds = new Set();
      if (order) {
        // V2: fetch just this question by order; total is always 2 for V2 questionnaires
        const orderResp = await QuestionnaireRepository.getAllQuestions(id, Number(order));
        if (orderResp && orderResp.questions) {
          const questionIdMap = {};
          orderResp.questions.forEach((d) => {
            questionIdMap[d.id] = { ...d };
            currentOrderQIds.add(String(d.id));
          });
          setResponseQuestionMap(questionIdMap);
          setAllQuestions([...orderResp.questions]);
        }
        setTotalQuestions(V2_TOTAL_QUESTIONS);
      } else {
        // V1: fetch all questions without order param (limit=1000 default)
        const allQuestionsResp = await QuestionnaireRepository.getAllQuestions(id);
        if (allQuestionsResp && allQuestionsResp.questions) {
          const questionIdMap = {};
          allQuestionsResp.questions.forEach((d) => {
            questionIdMap[d.id] = { ...d };
          });
          setResponseQuestionMap(questionIdMap);
          setAllQuestions([...allQuestionsResp.questions]);
          if (allQuestionsResp.total !== undefined) {
            setTotalQuestions(allQuestionsResp.total);
          }
        }
      }
      if (view && (view === 'readonly' || view === VIEW_MODE.EDIT_VIEW)) {
        let response;
        if (!responseId) {
          const resp = await QuestionnaireRepository
            .getRespondentResponses(id, respondentId);
          if (resp && resp.responses && Array.isArray(resp.responses) && resp.responses.length) {
            // TODO: showing only last response
            response = resp.responses[0];
          }
        } else {
          const resp = await QuestionnaireRepository
            .getResponseById(responseId);
          if (resp && resp.response) {
            response = resp.response;
          }
        }
        setRespondentResponses({ ...response });
        // When the user navigates directly to order>1 in edit mode (via startPage:N from the
        // parent app), sessionStorage for earlier orders is empty because clearAllOrderKeys ran
        // after the previous submission. Pre-populate each earlier order's key from the existing
        // server response so the final submit can collect a complete payload.
        if (order && Number(order) > 1 && view === VIEW_MODE.EDIT_VIEW
            && response && Array.isArray(response.responses)) {
          const prevResponses = response.responses.filter(
            (r) => !currentOrderQIds.has(String(r.questionId)),
          );
          if (prevResponses.length > 0) {
            const o1Key = getOrderStorageKey(id, respondentId, 1);
            // Only seed it when it's missing — don't overwrite if the user filled order=1 this session
            if (!sessionStorage.getItem(o1Key)) {
              sessionStorage.setItem(o1Key, JSON.stringify(prevResponses));
            }
          }
        }
      }
      // Answers for orders already visited live only in sessionStorage until the whole
      // attempt is submitted, so a resumed or reviewed question has to restore from there to
      // show the option the user already picked.
      if (order) {
        try {
          const cached = JSON.parse(
            sessionStorage.getItem(getOrderStorageKey(id, respondentId, Number(order))) || 'null',
          );
          if (Array.isArray(cached) && cached.length > 0) {
            setRespondentResponses((prev) => ({
              ...(prev || {}),
              responses: [...((prev && prev.responses) || []), ...cached],
            }));
          }
        } catch (err) {
          // A malformed cache entry just means no answer to restore.
        }
      }
      setLoading(false);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    (async function onSearch() {
      if (params && params.id && search) {
        const searchPar = getQueryParams(search);
        const {
          respondentId, view, responseId, order, enableForward,
        } = searchPar;
        setSearchParams(searchPar);
        if (!respondentId && view !== VIEW_MODE.FORM_VIEW) {
          setError('Respondent Id not found');
        } else {
          // enableForward is only set while the user sits behind their furthest question, which
          // is exactly when arriving at order 1 is a review rather than a fresh attempt.
          await setQuestionnaireData(
            params.id, respondentId, view, responseId, order, enableForward === 'true',
          );
        }
      } else {
        setError('Respondent Id not found');
      }
    }());
  }, [params, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (responses, changed = true) => {
    const { id } = params;
    const {
      respondentId, respondedBy, order,
    } = searchParams;
    const htmlframeElement = window.parent;

    // ── Paginated mode (order param present) ──────────────────────────────────
    if (order) {
      const currentOrder = Number(order);

      // Is this truly the last question?
      const isActuallyLastPage = totalQuestions > 0
        ? currentOrder >= totalQuestions
        : true;

      if (!isActuallyLastPage) {
        // Intermediate order — save THIS order's responses under its own key.
        // Going back and re-answering overwrites only this key, leaving others intact.
        sessionStorage.setItem(
          getOrderStorageKey(id, respondentId, currentOrder),
          JSON.stringify(responses),
        );
        htmlframeElement.postMessage({ type: 'SUBMIT_INIT' }, '*');
        htmlframeElement.postMessage({ type: 'SUBMIT_SUCCESS', data: { total: totalQuestions, changed } }, '*');
        return;
      }

      // Last order — collect all previous orders from storage + current, submit in one call
      const allResponses = [];
      for (let o = 1; o < currentOrder; o += 1) {
        const stored = JSON.parse(
          sessionStorage.getItem(getOrderStorageKey(id, respondentId, o)) || '[]',
        );
        allResponses.push(...stored);
      }
      allResponses.push(...responses);

      // Clean up all per-order keys
      clearAllOrderKeys(id, respondentId);

      setFormLoading(true);
      htmlframeElement.postMessage({ type: 'SUBMIT_INIT' }, '*');
      try {
        const payload = {
          response: {
            respondentId,
            responses: allResponses,
            metadata: {},
            respondedBy,
          },
        };
        const resp = await QuestionnaireRepository.createResponse(id, payload);
        htmlframeElement.postMessage({ type: 'SUBMIT_SUCCESS', data: { ...resp, total: totalQuestions, changed } }, '*');
        setModalState(true);
      } catch (e) {
        htmlframeElement.postMessage({ type: 'SUBMIT_FAILURE', data: e.message }, '*');
      }
      setFormLoading(false);
      return;
    }

    // ── Non-paginated mode (original behaviour) ────────────────────────────────
    setFormLoading(true);
    htmlframeElement.postMessage({ type: 'SUBMIT_INIT' }, '*');
    const metadata = JSON.parse(JSON.stringify(searchParams));
    delete metadata.view;
    delete metadata.respondentId;
    delete metadata.hideHeader;
    delete metadata.respondedBy;
    try {
      const payload = {
        response: {
          respondentId,
          responses,
          metadata,
          respondedBy,
        },
      };
      const resp = await QuestionnaireRepository.createResponse(id, payload);
      htmlframeElement.postMessage({ type: 'SUBMIT_SUCCESS', data: { ...resp, total: totalQuestions, changed } }, '*');
      setModalState(true);
    } catch (e) {
      htmlframeElement.postMessage({ type: 'SUBMIT_FAILURE', data: e.message }, '*');
    }
    setFormLoading(false);
  };

  const getRenderComponent = () => {
    const {
      respondentId, view, hideHeader, type, version, enableBack, isLast, genderType,
      order, questionNumber, totalCount, isMobile, tapToAdvance, enableForward,
    } = searchParams;
    let el = null;
    const ifData = questionnaire.title && Array.isArray(questions);
    const showHeader = hideHeader === 'true' || hideHeader === 'false' ? JSON.parse(hideHeader) : false;
    // In paginated mode, override isLast with the value calculated from actual total.
    // isLast from the URL includes the isLastAttribute check from the parent; we AND it
    // with isActuallyLastPage so the Submit button only shows on the true last question.
    const isActuallyLastPage = (order && totalQuestions > 0)
      ? Number(order) >= totalQuestions
      : true;
    const effectiveIsLast = order
      ? isActuallyLastPage && isLast === 'true'
      : isLast === 'true';
    if (ifData) {
      if (view && view === VIEW_MODE.FORM_VIEW) {
        el = (
          <FormView
            isLoading={formloader}
            questionnaire={questionnaire}
            questions={questions}
            showHeader={!showHeader}
            language={lang}
          />
        );
      } else if (respondentId) {
        if (type === VIEW_MODE.FORM_TYPE.REVIEW) {
          if (view && view === VIEW_MODE.READ_ONLY) {
            el = (
              <ReviewRespondentFormReadonly
                questionnaire={questionnaire}
                response={respondentResponses}
                questionIdMap={responseQuestionMap}
                showHeader={!showHeader}
              />
            );
          } else {
            el = (
              <ReviewRespondentForm
                isLoading={formloader}
                questionnaire={questionnaire}
                questions={questions}
                onSubmit={onSubmit}
                showHeader={!showHeader}
                language={lang}
              />
            );
          }
        } else if (view && view === VIEW_MODE.EDIT_VIEW) {
          el = (
            <RespondentForm
              isLoading={formloader}
              questionnaire={questionnaire}
              questions={questions}
              onSubmit={onSubmit}
              showHeader={!showHeader}
              language={lang}
              response={respondentResponses}
              questionIdMap={responseQuestionMap}
              version={version}
              enableBack={enableBack === 'true'}
              isLast={effectiveIsLast}
              genderType={genderType}
              questionNumber={questionNumber ? Number(questionNumber) : undefined}
              overallTotalCount={totalCount ? Number(totalCount) : undefined}
              isMobile={isMobile === 'true'}
              tapToAdvance={tapToAdvance === 'true'}
              enableForward={enableForward === 'true'}
            />
          );
        } else if (view && view === VIEW_MODE.READ_ONLY) {
          el = (
            <RespondentFormReadonly
              questionnaire={questionnaire}
              response={respondentResponses}
              questionIdMap={responseQuestionMap}
              showHeader={!showHeader}
              language={lang}
            />
          );
        } else {
          el = (
            <RespondentForm
              isLoading={formloader}
              questionnaire={questionnaire}
              questions={questions}
              onSubmit={onSubmit}
              showHeader={!showHeader}
              language={lang}
              version={version}
              enableBack={enableBack === 'true'}
              isLast={effectiveIsLast}
              genderType={genderType}
              questionNumber={questionNumber ? Number(questionNumber) : undefined}
              overallTotalCount={totalCount ? Number(totalCount) : undefined}
              isMobile={isMobile === 'true'}
              tapToAdvance={tapToAdvance === 'true'}
              enableForward={enableForward === 'true'}
              // Needed so a resumed/reviewed question shows the answer already picked; mid-attempt
              // that answer only exists in sessionStorage, never on the server yet.
              response={respondentResponses}
              questionIdMap={responseQuestionMap}
            />
          );
        }
      }
    } else {
      el = <ErrorComponent history={history} />;
    }
    return el;
  };

  const onHandleModalYes = () => {
    setModalState(false);
    const htmlframeElement = window.parent;
    htmlframeElement.postMessage({ type: 'RESPONSE_RECORDED' }, '*');
    window.postMessage({ type: 'RESPONSE_RECORDED' }, '*');
    history.push(`${ROUTE_NAME.submitted}?language=${lang}`);
  };
  return (
    <div ref={targetRef}>
      {loading ? (
        <div className="loaderCont">
          <CircularProgress size={UI_CONFIG.loaderSize} />
        </div>
      )
        : error === '' ? getRenderComponent()
          : <ErrorComponent history={history} />}
      <SuccessModal
        showModal={showModal}
        title={userConstants(lang).successHeader}
        primaryContext={userConstants(lang).submitResponseSuccess}
        handleYes={onHandleModalYes}
        okButtonText={userConstants(lang).okayBtn}
      />
    </div>
  );
};

export default memo(QuestionnaireForm);
